import { gsap } from "gsap";
import EmblaCarousel from "embla-carousel";
import ClassNames from "embla-carousel-class-names";
import { EmblaDotButtons } from "./components/emblaCarouselDotButton.js";
import AutoScroll from "embla-carousel-auto-scroll";
import { WheelGesturesPlugin } from "embla-carousel-wheel-gestures";
import { Guides } from "./components/guides.js";
import { OldBrowserChecker } from "./components/oldBrowserChecker.js";
import { EqualHeight } from "./components/equalHeight.js";
import { Tabs } from "./components/tabs.js";

/* -------------------------------------------------- */
/* 🙂 General */
/* -------------------------------------------------- */

gsap.defaults({
  duration: 0.25,
  ease: "power1.inOut",
});

const isReducedMotion = () => {
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
};

const isDesktop = () => {
  return window.matchMedia("(min-width: 1000px)").matches;
};

/* -------------------------------------------------- */
/* 👵 Check for old browsers */
/* -------------------------------------------------- */

new OldBrowserChecker();

/* -------------------------------------------------- */
/* 📏 Guides */
/* -------------------------------------------------- */

new Guides();

/* -------------------------------------------------- */
/* 🍱 Menu */
/* -------------------------------------------------- */

const menuToggle = document.querySelector(".menu-toggle");
const menu = document.querySelector(".menu");
const menuItems = document.querySelectorAll(".menu-item");

const openMenu = () => {
  menuToggle.setAttribute("aria-expanded", "true");

  // Fade only — no translate, which would lift the panel over the banner.
  gsap.fromTo(
    menu,
    {
      autoAlpha: 0,
    },
    {
      autoAlpha: 1,
      duration: 0.25,
    }
  );
};

const closeMenu = () => {
  menuToggle.setAttribute("aria-expanded", "false");

  gsap.to(menu, {
    autoAlpha: 0,
    duration: 0.25,
  });
};

menuToggle.addEventListener("click", () => {
  if (menuToggle.getAttribute("aria-expanded") === "true") {
    closeMenu();
  } else {
    openMenu();
  }
});

// Click outside to close
document.addEventListener("click", (event) => {
  if (menuToggle.getAttribute("aria-expanded") === "true") {
    if (!menu.contains(event.target) && !menuToggle.contains(event.target)) {
      closeMenu();
    }
  }
});

// Close on escape
document.addEventListener("keydown", (event) => {
  if (menuToggle.getAttribute("aria-expanded") === "true") {
    if (event.key === "Escape") {
      closeMenu();
    }
  }
});

menuItems.forEach((item) => {
  const submenu = item.querySelector(".submenu");
  const submenuToggle = item.querySelector(".menu-item__submenu-toggle");
  const submenuItems = item.querySelectorAll(".submenu__link");

  if (!submenu) {
    return;
  }

  const openSubmenu = () => {
    submenuToggle.setAttribute("aria-expanded", "true");
    submenu.setAttribute("aria-hidden", "false");
  };

  const closeSubmenu = () => {
    submenuToggle.setAttribute("aria-expanded", "false");
    submenu.setAttribute("aria-hidden", "true");
  };

  submenuToggle.addEventListener("click", () => {
    if (submenuToggle.getAttribute("aria-expanded") === "true") {
      closeSubmenu();
    } else {
      openSubmenu();
    }
  });

  submenuItems.forEach((submenuItem) => {
    submenuItem.addEventListener("click", () => {
      closeMenu();
    });
  });
});

/* -------------------------------------------------- */
/* 🎢 Sliders */
/* -------------------------------------------------- */

const sliders = document.querySelectorAll(".slider");

sliders.forEach((slider) => {
  const embla = EmblaCarousel(
    slider,
    {
      dragThreshold: 1,
      slidesToScroll: "auto",
      inViewThreshold: 0.5,
      watchDrag: (embla) => embla.internalEngine().scrollSnaps.length > 1,
    },
    [
      WheelGesturesPlugin({
        forceWheelAxis: "x",
      }),
      ClassNames(),
    ]
  );

  const dotButtons = new EmblaDotButtons(embla, slider);
});

/* -------------------------------------------------- */
/* 🎢 Logo Sliders */
/* -------------------------------------------------- */

const logoSliders = document.querySelectorAll(".logo-slider");

logoSliders.forEach((logoSlider) => {
  // Embla silently disables loop when slides don't overflow the container,
  // making AutoScroll stop at the end. Clone slides until they overflow at
  // any realistic viewport width so the loop always engages.
  const container = logoSlider.querySelector(".embla__container");
  const originalSlides = [...container.children];
  const contentWidth = () =>
    [...container.children].reduce((sum, slide) => sum + slide.offsetWidth, 0);

  while (
    originalSlides.length &&
    contentWidth() < logoSlider.clientWidth * 2
  ) {
    originalSlides.forEach((slide) => {
      const clone = slide.cloneNode(true);
      clone.setAttribute("aria-hidden", "true");
      container.appendChild(clone);
    });
  }

  const embla = EmblaCarousel(
    logoSlider,
    {
      loop: true,
      slidesToScroll: "auto",
      watchDrag: false,
    },
    [
      AutoScroll({
        startDelay: 0,
        speed: 1,
        stopOnInteraction: false,
        stopOnMouseEnter: false,
        stopOnFocusIn: false,
      }),
    ]
  );

  const autoScroll = embla?.plugins().autoScroll;

  const pauseButton = logoSlider
    .closest(".module")
    .querySelector(".pause-button");

  pauseButton.addEventListener("click", () => {
    if (!autoScroll) return;

    if (autoScroll.isPlaying()) {
      autoScroll.stop();
      pauseButton.setAttribute("aria-pressed", "true");
    } else {
      autoScroll.play();
      pauseButton.setAttribute("aria-pressed", "false");
    }
  });

  const togglePauseButton = (embla) => {
    pauseButton.setAttribute("aria-hidden", embla.scrollSnapList().length <= 1);
  };

  togglePauseButton(embla);

  embla.on("resize", () => {
    togglePauseButton(embla);
  });
});

/* -------------------------------------------------- */
/* 💬 Dialogs */
/* -------------------------------------------------- */

const dialogButtons = document.querySelectorAll("[data-dialog]");
const canonicalUrl = document.querySelector("link[rel='canonical']").href;

dialogButtons.forEach((button) => {
  let dialog = document.querySelector("dialog#" + button.dataset.dialog);

  if (dialog === null) {
    return;
  }

  let closeButton = dialog.querySelector(".modal__close-button");

  dialog.addEventListener("close", () => {
    history.pushState(null, null, canonicalUrl);
  });

  button.addEventListener("click", () => {
    history.pushState(null, null, "#" + dialog.id);
    dialog.showModal();
  });

  closeButton.addEventListener("click", () => {
    dialog.close();
  });

  dialog.addEventListener("click", (event) => {
    if (event.target === dialog || event.target.matches("a[href]")) {
      dialog.close();
    }
  });
});

// Check window hash for dialog
if (window.location.hash) {
  try {
    let dialogButton = document.querySelector(
      '[data-dialog="' + window.location.hash.substr(1) + '"]'
    );
    dialogButton.scrollIntoView();
    dialogButton.dispatchEvent(new Event("click"));
  } catch (error) {
    // console.log(error);
  }
}

/* -------------------------------------------------- */
/* Spacing shenanigans */
/* -------------------------------------------------- */

const modules = document.querySelectorAll(".module");
let lastBackgroundColor = null;

modules.forEach((module) => {
  let moduleBackgroundColor = gsap.getProperty(module, "--background-color");

  if (moduleBackgroundColor === lastBackgroundColor) {
    module.classList.add("module--no-top-padding");
  }

  lastBackgroundColor = moduleBackgroundColor;
});

/* -------------------------------------------------- */
/* 🪗 Accordion */
/* -------------------------------------------------- */

// Select all accordion elements
const accordions = document.querySelectorAll(".accordion-item");

// Iterate over each accordion
accordions.forEach((accordion) => {
  // Select the title, arrow, and content of the accordion item
  let title = accordion.querySelector(".accordion-item__title");
  let arrow = accordion.querySelector("svg");
  let content = accordion.querySelector(".accordion-item__content");

  // Create a timeline for the accordion animation
  let accordionAnimation = gsap.timeline({
    paused: true,
    defaults: {
      ease: "power3.inOut",
      duration: isReducedMotion() ? 0 : 0.75,
    },
  });

  accordionAnimation
    .to(
      content,
      {
        height: "auto",
        autoAlpha: 1,
        duration: isReducedMotion() ? 0 : 0.5,
        immediateRender: false,
      },
      0
    )
    .to(
      arrow,
      {
        rotate: -180,
        ease: "back.inOut",
        duration: isReducedMotion() ? 0 : 0.5,
      },
      0
    );

  accordionAnimation.reverse();

  // Add a click event listener to the title
  title.addEventListener("click", () => {
    // Check if the accordion item is expanded
    const expanded = title.getAttribute("aria-expanded") === "true";

    // Toggle the aria-expanded attribute
    title.setAttribute("aria-expanded", !expanded);

    if (accordionAnimation.reversed()) {
      // Play the animation or apply changes instantly
      accordionAnimation.play();

      // Update the URL with the accordion item's ID
      history.replaceState(null, null, `#${accordion.id}`);
    } else {
      // Reverse the animation or apply changes instantly
      accordionAnimation.reverse();

      // Update the URL to remove the accordion item's ID
      history.replaceState(null, null, window.location.pathname);
    }
  });
});

if (accordions.length) {
  // Check for hash
  const hash = window.location.hash;
  if (hash) {
    try {
      const element = document.querySelector(hash);
      if (element?.matches(".accordion-item")) {
        element
          .querySelector(".accordion-item__title")
          ?.dispatchEvent(new Event("click"));
      }
    } catch (err) {
      console.error(err);
    }
  }
}

/* -------------------------------------------------- */
/* Back to top button  */
/* -------------------------------------------------- */

const backToTopButton = document.querySelector(".back-to-top");

// Hide button initially
gsap.set(backToTopButton, {
  yPercent: isReducedMotion() ? 0 : 50,
});

// Debounced scroll handler
window.addEventListener(
  "scroll",
  () => {
    const shouldShow = window.scrollY > 500;

    if (shouldShow) {
      gsap.to(backToTopButton, {
        opacity: 1,
        yPercent: 0,
        duration: 0.25,
        ease: "power2.out",
      });
    } else {
      gsap.to(backToTopButton, {
        opacity: 0,
        yPercent: isReducedMotion() ? 0 : 50,
        duration: 0.25,
        ease: "power2.out",
      });
    }
  },
  { passive: true }
);

// Smooth scroll to top when clicked
backToTopButton.addEventListener("click", (e) => {
  window.scrollTo({
    top: 0,
    behavior: "smooth",
  });

  // Focus on the first focusable element
  let header = document.querySelector(".header");
  header.tabIndex = -1;
  document.querySelector(".header").focus();
  header.removeAttribute("tabindex");

  e.preventDefault();
});

/* -------------------------------------------------- */
/* 🗂️ Tabs */
/* -------------------------------------------------- */

const tabGroups = document.querySelectorAll(".tabs");

tabGroups.forEach((tabGroup) => {
  new Tabs(tabGroup);
});

/* -------------------------------------------------- */
/* 🃏 Cards */
/* -------------------------------------------------- */

// Equal height for headlines

const cardsContainers = document.querySelectorAll(".cards");

cardsContainers.forEach((cardsContainer) => {
  let headlines = cardsContainer.querySelectorAll(".card__headline");

  if (headlines.length) {
    new EqualHeight(headlines);
  }
});
